import React from 'react';

function getStickyHeaders() {
    return document.querySelectorAll('header .sticky, .sidebar-simple, .content-editor-topbar__wrapper');
}

export function getFixedHeaderHeight() {
    const headers = getStickyHeaders();
    if (!headers.length) {
        return 0;
    }
    // Combine height of all children
    let height = 0;
    for (const child of Array.from(headers)) {
        height += child.getBoundingClientRect().height;
    }
    return height;
}

// Shared across every mounted instance, so N components only trigger one DOM read per event.
let currentHeight = 0;
const subscribers = new Set<() => void>();
let rafId: number | null = null;
let removeListeners: (() => void) | null = null;

function measure() {
    const headerHeight = getFixedHeaderHeight();
    // Ignore a zero reading (nothing measurable yet) and skip notifying if nothing actually changed.
    if (headerHeight && headerHeight !== currentHeight) {
        currentHeight = headerHeight;
        subscribers.forEach((notify) => notify());
    }
}

function scheduleMeasure() {
    // Throttle via requestAnimationFrame so several events in one frame trigger one measurement.
    if (rafId === null) {
        rafId = requestAnimationFrame(() => {
            rafId = null;
            measure();
        });
    }
}

function subscribe(notify: () => void) {
    subscribers.add(notify);

    // Reference-counted: first subscriber wires up the listeners, last one tears them down.
    if (!removeListeners) {
        window.addEventListener('scroll', scheduleMeasure, { passive: true });
        window.addEventListener('resize', scheduleMeasure);
        removeListeners = () => {
            window.removeEventListener('scroll', scheduleMeasure);
            window.removeEventListener('resize', scheduleMeasure);
        };
    }

    return () => {
        subscribers.delete(notify);
        if (subscribers.size === 0) {
            removeListeners?.();
            removeListeners = null;
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
            // Drop the last reading so a later surface doesn't inherit this page's header height.
            currentHeight = 0;
        }
    };
}

function getSnapshot() {
    return currentHeight;
}

/**
 * Utility hook returning the current fixed header height in pixel.
 */
export function useHeaderFixedHeight() {
    // Measure eagerly at mount so we're correct without waiting for a scroll/resize event.
    React.useLayoutEffect(() => {
        if (currentHeight) {
            // Already measured — refresh next frame in case the header stack grew since.
            scheduleMeasure();
        } else {
            measure();
        }
    }, []);

    return React.useSyncExternalStore(subscribe, getSnapshot);
}
